import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../data/account.ts';
import { useSession } from '../../data/session.tsx';
import { withSessionLock } from '../../data/session-lock.ts';
import { useApp } from '../../data/store.tsx';
import { publicationFromPicks } from '../../engine/publication.ts';
import { fmtRange } from '../../engine/time.ts';
import type { Day } from '../../engine/types.ts';

type Publication = Parameters<typeof api.publish>[0];

export function ShareDay({ day }: { day: Day }) {
  const { state, derived } = useApp();
  const { session, trusted } = useSession();
  const user = session?.user;
  const sets = publicationFromPicks(day, derived.picksByDay.get(day.id) ?? [], derived.artistById);
  const current: Publication = {
    ownerKind: 'user', festivalId: state.festival!.id, dayId: day.id, dayLabel: day.label,
    title: `${user?.displayName ?? ''}, ${day.label}`, blurb: session?.profile?.bio ?? '', sets,
  };
  const signature = JSON.stringify(current);
  const latest = useRef(signature);
  latest.current = signature;
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const [review, setReview] = useState<Publication | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const busyRef = useRef(false);
  const stale = review !== null && JSON.stringify(review) !== signature;
  const publicProfile = session?.profile?.visibility === 'public';

  const submit = async (remove = false) => {
    if (!trusted || !user || busyRef.current || (!remove && (!review || !confirmed || stale || !publicProfile))) return;
    busyRef.current = true;
    setBusy(true);
    setNote('');
    const reviewed = review;
    try {
      await withSessionLock(async () => {
        if (!active.current) return;
        const identity = await api.session();
        if (!active.current) return;
        if (!identity.ok || identity.data?.user?.id !== user.id) {
          setNote('The account could not be confirmed. Sign in again before sharing.');
          return;
        }
        if (!remove && (identity.data.profile?.visibility !== 'public' ||
          (identity.data.profile.bio ?? '') !== reviewed?.blurb ||
          `${identity.data.user.displayName}, ${day.label}` !== reviewed?.title ||
          JSON.stringify(reviewed) !== latest.current)) {
          setNote('Your plan or profile changed. Review it again before publishing.');
          setConfirmed(false);
          return;
        }
        const result = remove ? await api.unpublish(day.id, 'user') : await api.publish(reviewed!);
        if (!active.current) return;
        setNote(result.ok ? (remove ? `${day.label} was removed from the public index.` : `${day.label} was published with the reviewed picks.`)
          : (result.error ?? (remove ? 'That itinerary was not removed.' : 'That itinerary did not publish.')));
        if (result.ok) { setReview(null); setConfirmed(false); }
      });
    } finally { busyRef.current = false; if (active.current) setBusy(false); }
  };

  return <section className="panel" aria-label="Share itinerary">
    <h3>Share {day.label}</h3>
    <p className="muted">Publish a snapshot of your chosen sets so people can find your itinerary. Later edits stay private until you review and publish again.</p>
    {!user ? <Link to="/account" state={{ from: '/plan' }}>Sign in to share your picks</Link> : <>
      {!publicProfile && <p>Your profile is private. <Link to="/profile?edit=privacy">Review profile privacy</Link> before publishing.</p>}
      {sets.length === 0 && <p>Choose sets in the planner before sharing this day.</p>}
      {sets.length > 60 && <p>This day exceeds the publication limit of 60 sets.</p>}
      <button className="small" disabled={busy || !trusted || !publicProfile || sets.length === 0 || sets.length > 60}
        onClick={() => { setReview(current); setConfirmed(false); setNote(''); }}>Review picks to publish</button>
      {review && <div>
        <h4>{review.title}</h4>
        {review.blurb && <p>{review.blurb}</p>}
        <div className="rowlist">{review.sets.map((pick) => <div className="row" key={pick.setId}>
          <span>{pick.artistName}<span className="sub" style={{ display: 'block' }}>
            {derived.stageById.get(pick.stageId)?.name} · {fmtRange(pick.startTs, pick.endTs)}
          </span></span>
        </div>)}</div>
        {stale && <p role="status">Your picks changed. Review the current picks again before publishing.</p>}
        <label className="flex wrap"><input type="checkbox" checked={confirmed && !stale} disabled={stale || busy}
          onChange={(event) => setConfirmed(event.target.checked)} />I reviewed these {review.sets.length} picks and their times.</label>
        <div className="flex wrap">
          <button className="primary small" disabled={!confirmed || stale || busy || !trusted || !publicProfile}
            onClick={() => void submit()}>Publish reviewed picks</button>
          <button className="small ghost" disabled={busy} onClick={() => { setReview(null); setConfirmed(false); }}>Cancel</button>
        </div>
      </div>}
      <button className="small ghost" disabled={busy || !trusted} onClick={() => void submit(true)}>Remove published {day.label}</button>
    </>}
    {note && <p role="status">{note}</p>}
  </section>;
}
